/**
 * 백엔드(7001) 호출 한 곳. 화면은 이것을 직접 쓰지 않고 services/*Api.ts 를 쓴다.
 *
 * 주소는 REACT_APP_API_URL — 비우면 http://localhost:7001/api.
 * 실패하면 서버가 준 message 를 그대로 던진다(DB 제약 위반은 400 + 제약 이름).
 */
import { CONSTRAINT_TEXT } from '../lib/errors';
import { token } from './token';

export const BASE = (process.env.REACT_APP_API_URL || 'http://localhost:7001/api').replace(/\/$/, '');

export class ApiError extends Error {
  constructor(public status: number, message: string, public constraint?: string | null) { super(message); }
}

async function req<T>(method: string, path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    const t = token.get();
    const headers: Record<string, string> = body instanceof FormData ? {} : { 'Content-Type': 'application/json' };
    if (t) headers.Authorization = `Bearer ${t}`;
    res = await fetch(BASE + path, {
      method,
      headers,
      body: body === undefined ? undefined : body instanceof FormData ? body : JSON.stringify(body),
    });
  } catch {
    // fetch 자체가 실패 — 서버가 꺼졌거나 주소가 틀렸다. 「Failed to fetch」 대신 무엇을 볼지 말한다
    throw new ApiError(0, `서버에 연결하지 못했습니다 — 백엔드(${BASE})가 떠 있는지 보세요`);
  }
  // 토큰이 끝났거나 권한이 회수됐다 — 로그인 화면으로(AuthContext 가 듣는다). 로그인 · 바로 이동의 401 은 「틀렸다」일 뿐이다
  if (res.status === 401 && !path.startsWith('/auth/login') && !path.startsWith('/auth/sso')) {
    token.clear();
    window.dispatchEvent(new Event('auth:expired'));
  }
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const msg = Array.isArray(data?.message) ? data.message.join(', ') : data?.message || `요청 실패 (${res.status})`;
    // 제약 이름은 사람이 읽을 말로 — 모르는 제약만 이름을 덧붙인다(어디서 막혔는지 찾을 수 있게)
    const text = data?.constraint ? CONSTRAINT_TEXT[data.constraint] ?? `${msg} (${data.constraint})` : msg;
    throw new ApiError(res.status, text, data?.constraint);
  }
  return data as T;
}

const qs = (q?: Record<string, string | number | null | undefined>) => {
  if (!q) return '';
  const p = Object.entries(q).filter(([, v]) => v !== undefined).map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v === null ? 'null' : String(v))}`);
  return p.length ? `?${p.join('&')}` : '';
};

/** 파일 내려받기 — 토큰이 필요해서 <a href> 로는 안 된다. 받아서 저장한다 */
export async function download(path: string, fallbackName: string) {
  const t = token.get();
  const res = await fetch(BASE + path, { headers: t ? { Authorization: `Bearer ${t}` } : {} });
  if (!res.ok) throw new ApiError(res.status, res.status === 404 ? '파일이 없습니다' : `내려받지 못했습니다 (${res.status})`);
  const cd = res.headers.get('content-disposition') ?? '';
  const m = /filename\*=UTF-8''([^;]+)/i.exec(cd) ?? /filename="?([^";]+)"?/i.exec(cd);
  const name = m ? decodeURIComponent(m[1]) : fallbackName;
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url; a.download = name; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export const http = {
  get: <T,>(path: string, q?: Record<string, string | number | null | undefined>) => req<T>('GET', path + qs(q)),
  post: <T,>(path: string, body?: unknown) => req<T>('POST', path, body),
  patch: <T,>(path: string, body: unknown) => req<T>('PATCH', path, body),
  put: <T,>(path: string, body: unknown) => req<T>('PUT', path, body),
  del: <T,>(path: string, q?: Record<string, string | number>) => req<T>('DELETE', path + qs(q)),
};

/** 테이블 하나를 그대로 — /api/<자원> */
export function resource<T extends { id?: number }>(name: string) {
  return {
    list: (q?: Record<string, string | number | null | undefined>) => http.get<T[]>(`/${name}`, q),
    get: (id: number) => http.get<T>(`/${name}/${id}`),
    create: (body: Partial<T>) => http.post<T>(`/${name}`, body),
    update: (id: number, body: Partial<T>) => http.patch<T>(`/${name}/${id}`, body),
    remove: (id: number) => http.del<{ ok: true }>(`/${name}/${id}`),
  };
}

/** DB 는 영문 코드, 화면은 한글 — 양쪽으로 바꾼다 */
export function codeMap<K extends string, V extends string>(pairs: Record<K, V>) {
  const back = Object.fromEntries(Object.entries(pairs).map(([k, v]) => [v, k])) as Record<V, K>;
  return {
    toUi: (k: string | null | undefined): V | null => (k == null ? null : (pairs as any)[k] ?? null),
    toDb: (v: string | null | undefined): K | null => (v == null || v === '' ? null : (back as any)[v] ?? null),
  };
}
